/**
 * SEO utilities and helpers for Nortiq Blog
 */

export interface SEOData {
  title: string;
  description: string;
  keywords: string;
  image: string;
  url: string;
  type: 'website' | 'article' | 'product';
  siteName?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  twitterHandle?: string;
}

/**
 * Generate optimized page title
 */
export const generatePageTitle = (title: string, siteName: string): string => {
  // Fallbacks
  if (!title && siteName) return siteName;
  if (!siteName && title) return title;

  const MAX_LEN = 60; // Recommended length

  // Helper: case-insensitive includes
  const ciIncludes = (a: string, b: string) => a.toLowerCase().includes(b.toLowerCase());

  // Derive a brand token from siteName (e.g., "Nortiq.ca" from "Nortiq.ca - Product Reviews")
  const brandToken = siteName.split(/\s[|\-–]\s/)[0] || siteName;

  // If title already contains the full siteName or brand token, don't append again
  if (ciIncludes(title, siteName) || ciIncludes(title, brandToken)) {
    // Truncate the final title safely to MAX_LEN if needed
    if (title.length > MAX_LEN) {
      return title.substring(0, MAX_LEN - 3) + '...';
    }
    return title;
  }

  // Compose full title first, then truncate to MAX_LEN by trimming the title portion
  const separator = ' | ';
  let full = `${title}${separator}${siteName}`;
  if (full.length <= MAX_LEN) return full;

  // Need to trim the title portion to fit within MAX_LEN including separator and siteName
  const budgetForTitle = MAX_LEN - (separator.length + siteName.length);
  if (budgetForTitle <= 0) {
    // Edge case: siteName itself exceeds or consumes the budget; fallback to siteName trimmed
    return siteName.length > MAX_LEN ? siteName.substring(0, MAX_LEN - 3) + '...' : siteName;
  }

  const trimmedTitle = title.length > budgetForTitle
    ? title.substring(0, Math.max(0, budgetForTitle - 3)) + '...'
    : title;

  full = `${trimmedTitle}${separator}${siteName}`;
  return full.length > MAX_LEN ? full.substring(0, MAX_LEN) : full;
};

/**
 * Generate optimized meta description
 */
export const generateMetaDescription = (description: string, maxLength: number = 160): string => {
  if (!description) return '';
  
  if (description.length <= maxLength) {
    return description;
  }
  
  // Truncate at word boundary
  const truncated = description.substring(0, maxLength - 3);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return truncated.substring(0, lastSpace) + '...';
};

/**
 * Generate keywords from content
 */
export const extractKeywords = (content: string, title: string, category?: string): string => {
  const commonWords = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by',
    'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did',
    'will', 'would', 'could', 'should', 'may', 'might', 'can', 'this', 'that', 'these', 'those'
  ]);
  
  // Combine title and content
  const text = `${title} ${content}`.toLowerCase();
  
  // Extract words
  const words = text.match(/\b[a-z]{3,}\b/g) || [];
  
  // Count word frequency
  const wordCount = new Map<string, number>();
  words.forEach(word => {
    if (!commonWords.has(word)) {
      wordCount.set(word, (wordCount.get(word) || 0) + 1);
    }
  });
  
  // Get top keywords
  const sortedWords = Array.from(wordCount.entries())
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([word]) => word);
  
  // Add category if provided
  if (category) {
    sortedWords.unshift(category.toLowerCase());
  }
  
  return sortedWords.join(', ');
};

/**
 * Generate structured data for blog posts
 */
export const generateBlogPostStructuredData = (post: {
  title: string;
  description: string;
  content: string;
  author: string;
  publishedAt: Date;
  updatedAt: Date;
  featuredImage?: string;
  category: string;
  tags: string[];
  url: string;
}, siteName?: string): string => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.description,
    "image": post.featuredImage || '',
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": siteName || "Nortiq.ca - Product Reviews",
      "logo": {
        "@type": "ImageObject",
        "url": "/logo192.png"
      }
    },
    "datePublished": post.publishedAt.toISOString(),
    "dateModified": post.updatedAt.toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": post.url
    },
    "articleSection": post.category,
    "keywords": post.tags.join(', '),
    "wordCount": post.content.replace(/<[^>]*>/g, '').split(/\s+/).length
  };
  
  return JSON.stringify(structuredData);
};

/**
 * Generate structured data for product reviews
 */
export const generateProductReviewStructuredData = (product: {
  name: string;
  description: string;
  image?: string;
  rating: number;
  reviewCount?: number;
  author: string;
  publishedAt: Date;
  url: string;
}, siteName?: string): string => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Review",
    "itemReviewed": {
      "@type": "Product",
      "name": product.name,
      "description": product.description,
      "image": product.image || ''
    },
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": product.rating,
      "bestRating": 5,
      "worstRating": 1
    },
    "author": {
      "@type": "Person",
      "name": product.author
    },
    "publisher": {
      "@type": "Organization",
      "name": siteName || "Nortiq.ca - Product Reviews"
    },
    "datePublished": product.publishedAt.toISOString(),
    "url": product.url
  };
  
  return JSON.stringify(structuredData);
};

/**
 * Generate breadcrumb structured data
 */
export const generateBreadcrumbStructuredData = (breadcrumbs: Array<{
  name: string;
  url: string;
}>): string => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  };
  
  return JSON.stringify(structuredData);
};

/**
 * Generate website structured data
 */
export const generateWebsiteStructuredData = (siteData: {
  name: string;
  description: string;
  url: string;
  logo: string;
  socialLinks: string[];
}): string => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": siteData.name,
    "description": siteData.description,
    "url": siteData.url,
    "logo": siteData.logo,
    "sameAs": siteData.socialLinks,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${siteData.url}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };
  
  return JSON.stringify(structuredData);
};

/**
 * Generate Open Graph tags
 */
export const generateOpenGraphTags = (seoData: SEOData): Array<{
  property: string;
  content: string;
}> => {
  const tags = [
    { property: 'og:title', content: seoData.title },
    { property: 'og:description', content: seoData.description },
    { property: 'og:type', content: seoData.type },
    { property: 'og:url', content: seoData.url },
    { property: 'og:site_name', content: seoData.siteName || 'Nortiq.ca - Product Reviews' }
  ];
  
  if (seoData.image) {
    const absImage = toAbsoluteUrl(seoData.image);
    tags.push({ property: 'og:image', content: absImage });
    tags.push({ property: 'og:image:alt', content: seoData.title });
    tags.push({ property: 'og:image:width', content: '1200' });
    tags.push({ property: 'og:image:height', content: '630' });
    tags.push({ property: 'og:image:type', content: 'image/jpeg' });
  }
  
  if (seoData.type === 'article') {
    if (seoData.author) {
      tags.push({ property: 'article:author', content: seoData.author });
    }
    if (seoData.publishedTime) {
      tags.push({ property: 'article:published_time', content: seoData.publishedTime });
    }
    if (seoData.modifiedTime) {
      tags.push({ property: 'article:modified_time', content: seoData.modifiedTime });
    }
    if (seoData.section) {
      tags.push({ property: 'article:section', content: seoData.section });
    }
    if (seoData.tags) {
      seoData.tags.forEach(tag => {
        tags.push({ property: 'article:tag', content: tag });
      });
    }
  }
  
  return tags;
};

/**
 * Generate Twitter Card tags
 */
export const generateTwitterCardTags = (seoData: SEOData): Array<{
  name: string;
  content: string;
}> => {
  const tags = [
    { name: 'twitter:card', content: seoData.image ? 'summary_large_image' : 'summary' },
    { name: 'twitter:title', content: seoData.title },
    { name: 'twitter:description', content: seoData.description },
    { name: 'twitter:site', content: seoData.twitterHandle || '' },
    { name: 'twitter:creator', content: seoData.twitterHandle || '' }
  ];
  
  if (seoData.image) {
    const absImage = toAbsoluteUrl(seoData.image);
    tags.push({ name: 'twitter:image', content: absImage });
    tags.push({ name: 'twitter:image:alt', content: seoData.title });
  }
  
  return tags;
};

/**
 * Generate canonical URL
 */
export const generateCanonicalUrl = (path: string, baseUrl?: string): string => {
  const envBase = (typeof window === 'undefined' ? process?.env?.VITE_SITE_URL : (window as any)?.VITE_SITE_URL) as string | undefined;
  const dynamicBaseUrl = baseUrl || envBase || (typeof window !== 'undefined' ? window.location.origin : 'https://nortiq.ca');
  // Remove trailing slash  // Ensure path starts with /
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  
  // Remove trailing slash from baseUrl and combine
  return `${dynamicBaseUrl.replace(/\/$/, '')}${cleanPath}`;
};

/** Ensure a URL is absolute using site base */
export const toAbsoluteUrl = (maybeUrl: string | undefined, base?: string): string => {
  if (!maybeUrl) return '';
  if (/^https?:\/\//i.test(maybeUrl)) return maybeUrl;
  const origin = base || (typeof window !== 'undefined' ? window.location.origin : 'https://nortiq.ca');
  const clean = maybeUrl.startsWith('/') ? maybeUrl : `/${maybeUrl}`;
  return `${origin}${clean}`;
};

/**
 * Validate SEO data
 */
export const validateSEOData = (seoData: Partial<SEOData>): {
  isValid: boolean;
  warnings: string[];
  errors: string[];
} => {
  const warnings: string[] = [];
  const errors: string[] = [];
  
  // Title validation
  if (!seoData.title) {
    errors.push('Title is required');
  } else if (seoData.title.length > 60) {
    warnings.push('Title is longer than 60 characters and may be truncated in search results');
  } else if (seoData.title.length < 10) {
    warnings.push('Title is shorter than 10 characters and may not be descriptive enough');
  }
  
  // Description validation
  if (!seoData.description) {
    errors.push('Description is required');
  } else if (seoData.description.length > 160) {
    warnings.push('Description is longer than 160 characters and may be truncated in search results');
  } else if (seoData.description.length < 50) {
    warnings.push('Description is shorter than 50 characters and may not be descriptive enough');
  }
  
  // URL validation
  if (!seoData.url) {
    errors.push('URL is required');
  }
  
  // Image validation
  if (seoData.image && !seoData.image.startsWith('http')) {
    warnings.push('Image URL should be absolute (start with http/https)');
  }
  
  return {
    isValid: errors.length === 0,
    warnings,
    errors
  };
};
